import { app } from "@microsoft/teams-js";
import { logger } from "./logger";


function isTeamsDesktopHost() {
  try {
    const clientType = (localStorage.getItem("teamsHostClientType") || "")
      .trim()
      .toLowerCase();
    if (clientType) {
      return clientType === "desktop";
    }
  } catch {
  }
  return (
    typeof navigator !== "undefined" &&
    /electron/i.test(navigator.userAgent || "")
  );
}

const OFFICE_DESKTOP_APPS = {
  word: { scheme: "ms-word", displayName: "Microsoft Word" },
  excel: { scheme: "ms-excel", displayName: "Microsoft Excel" },
  powerpoint: { scheme: "ms-powerpoint", displayName: "Microsoft PowerPoint" },
};


const OFFICE_DESKTOP_EXTS = [
  // Word
  ".doc", ".docx", ".docm", ".dot", ".dotx", ".dotm", ".rtf", ".odt",
  // Excel
  ".xls", ".xlsx", ".xlsm", ".xlsb", ".xlt", ".xltx", ".xltm", ".ods", ".csv",
  // PowerPoint
  ".ppt", ".pptx", ".pptm", ".pot", ".potx", ".potm", ".pps", ".ppsx", ".ppsm", ".odp",
];

/**
 *
 * @param {object | null | undefined} file document item (Graph driveItem shape)
 * @returns {string} direct file URL, or "" when none can be resolved
 */
export function resolveOfficeDesktopUrl(file) {
  if (!file || typeof file !== "object") {
    return "";
  }

  const webDavUrl =
    typeof file.webDavUrl === "string" ? file.webDavUrl.trim() : "";
  if (webDavUrl) {
    return webDavUrl;
  }

  const webUrl = file.webUrl ?? file.WebUrl;
  if (!webUrl || typeof webUrl !== "string" || !webUrl.trim()) {
    return "";
  }

  let parsed;
  try {
    parsed = new URL(webUrl.trim());
  } catch {
    return "";
  }

  const lowerPath = parsed.pathname.toLowerCase();
  const isDirectFileUrl = OFFICE_DESKTOP_EXTS.some((ext) =>
    lowerPath.endsWith(ext),
  );
  if (!isDirectFileUrl) {
    return "";
  }

  return parsed.origin + parsed.pathname;
}

/**
 * Builds the Office URI that opens a document for editing in the desktop app.
 *
 * The scheme is pipe-delimited (`<ms-app>:ofe|u|<url>`); the document URL is
 * appended verbatim and must NOT be percent-encoded, otherwise Office rejects it.
 *
 * @param {string | null | undefined} kind app kind ("word" | "excel" | "powerpoint")
 * @param {string | null | undefined} directUrl direct file URL (see resolveOfficeDesktopUrl)
 * @returns {string} the Office URI, or "" when kind/URL is unusable
 */
export function buildOfficeDesktopUri(kind, directUrl) {
  const app = OFFICE_DESKTOP_APPS[kind];
  if (!app || !directUrl || typeof directUrl !== "string") {
    return "";
  }
  const trimmed = directUrl.trim();
  if (!trimmed) {
    return "";
  }
  return `${app.scheme}:ofe|u|${trimmed}`;
}

/**
 *
 * @param {string} uri the custom-protocol URI to launch
 * @param {{ onUnavailable?: () => void, timeoutMs?: number | null }} [options]
 *   timeoutMs null skips the "did the app take over?" check: onUnavailable then
 *   only fires when the launch itself couldn't be attempted.
 */
function launchProtocolUri(uri, { onUnavailable, timeoutMs = 2500 } = {}) {
  let handoff = false;
  const markHandoff = () => {
    handoff = true;
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      markHandoff();
    }
  };

  window.addEventListener("blur", markHandoff);
  window.addEventListener("pagehide", markHandoff);
  document.addEventListener("visibilitychange", onVisibilityChange);

  const cleanup = () => {
    window.removeEventListener("blur", markHandoff);
    window.removeEventListener("pagehide", markHandoff);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  let anchorLaunched = false;
  try {
    const anchor = document.createElement("a");
    anchor.href = uri;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    anchorLaunched = true;
  } catch (err) {
    logger.warn("launchProtocolUri anchor click failed", err, "DESKTOP");
  }

  let openLinkAttempted = false;
  let windowOpenAttempted = false;
  if (isTeamsDesktopHost()) {
    try {
      const openLinkResult = app.openLink(uri);
      openLinkAttempted = true;
      if (openLinkResult && typeof openLinkResult.catch === "function") {
        openLinkResult.catch(() => {});
      }
    } catch {
      // Host rejected the scheme synchronously.
    }

    try {
      const popup = window.open(uri, "_blank");
      windowOpenAttempted = true;
      if (popup) {
        try {
          popup.close();
        } catch {
        }
      }
    } catch {
    }
  }

  logger.info(
    "launchProtocolUri",
    { uri, anchorLaunched, openLinkAttempted, windowOpenAttempted, timeoutMs },
    "DESKTOP",
  );

  if (!anchorLaunched && !openLinkAttempted && !windowOpenAttempted) {
    cleanup();
    if (typeof onUnavailable === "function") {
      onUnavailable();
    }
    return;
  }

  if (timeoutMs == null) {
    cleanup();
    return;
  }

  window.setTimeout(() => {
    cleanup();
    if (!handoff && typeof onUnavailable === "function") {
      onUnavailable();
    }
  }, timeoutMs);
}

/**
 * Percent-encodes everything except ASCII letters/digits, matching the format
 * OneDrive web itself emits for odopen links (it also encodes "-", ".", "_").
 */
function encodeOdopenValue(value) {
  return Array.from(new TextEncoder().encode(String(value)))
    .map((byte) =>
      /[A-Za-z0-9]/.test(String.fromCharCode(byte))
        ? String.fromCharCode(byte)
        : `%${byte.toString(16).toUpperCase().padStart(2, "0")}`,
    )
    .join("");
}

function wrapGuidInBraces(guid) {
  const trimmed = String(guid).trim().replace(/^\{|\}$/g, "");
  return `{${trimmed}}`;
}

/**
 * Builds the OneDrive sync app link that OneDrive web uses for "Open in app" on
 * non-Office files (e.g. images). The sync app brings the file down to the local
 * OneDrive folder, opens it in its default app (Photos for images) and syncs any
 * edits back. Format (undocumented, captured from OneDrive web):
 *   odopen://openFile/?fileId=<listItemUniqueId>&siteId={<siteId>}&listId={<listId>}
 *     &userEmail=<upn>&userId=<aad object id>&webUrl=<siteUrl>&fileName=<name>
 *
 * @param {object | null | undefined} file Graph driveItem with `sharepointIds` and `name`
 * @param {{ userEmail?: string | null, userId?: string | null }} user signed-in user
 * @returns {string} the odopen URI, or "" when a required value is missing
 */
export function buildOneDriveOpenFileUri(file, { userEmail, userId } = {}) {
  const ids = file?.sharepointIds;
  const fileName = file?.name;
  if (
    !ids?.listItemUniqueId ||
    !ids?.siteId ||
    !ids?.listId ||
    !ids?.siteUrl ||
    !fileName ||
    !userEmail ||
    !userId
  ) {
    return "";
  }

  const params = [
    ["fileId", String(ids.listItemUniqueId).replace(/^\{|\}$/g, "")],
    ["siteId", wrapGuidInBraces(ids.siteId)],
    ["listId", wrapGuidInBraces(ids.listId)],
    ["userEmail", userEmail],
    ["userId", userId],
    ["webUrl", ids.siteUrl],
    ["fileName", fileName],
  ];

  return `odopen://openFile/?${params
    .map(([key, value]) => `${key}=${encodeOdopenValue(value)}`)
    .join("&")}`;
}

/**
 * Opens a non-Office file (e.g. an image) in its default desktop app through the
 * OneDrive sync app, the same way OneDrive web's "Open in app" does.
 *
 * @param {object | null | undefined} file Graph driveItem with `sharepointIds` and `name`
 * @param {{ userEmail?: string | null, userId?: string | null, onUnavailable?: () => void }} [options]
 *   onUnavailable is called only when the link can't be built or launched. There is
 *   no timed fallback: the sync app can take several seconds to download the file
 *   before the desktop app takes focus, so a timer would also open the browser
 *   alongside Photos. The sync app itself opens the browser when it has no local
 *   copy (e.g. a Team library that isn't synced).
 * @returns {boolean} true when a launch was attempted, false when input was unusable
 */
export function openInOneDriveSyncApp(file, { userEmail, userId, onUnavailable } = {}) {
  const uri = buildOneDriveOpenFileUri(file, { userEmail, userId });
  if (!uri) {
    if (typeof onUnavailable === "function") {
      onUnavailable();
    }
    return false;
  }
  launchProtocolUri(uri, { onUnavailable, timeoutMs: null });
  return true;
}

/**
 * Opens a document in the matching Office desktop application.
 *
 * @param {string | null | undefined} kind app kind ("word" | "excel" | "powerpoint")
 * @param {string | null | undefined} directUrl direct URL of the document to edit
 * @param {{ onUnavailable?: () => void }} [options]
 *   onUnavailable is called when the app appears not to be installed or the desktop
 *   app could not be opened (used to surface a message to the user).
 * @returns {boolean} true when a launch was attempted, false when input was unusable
 */
export function openInOfficeDesktop(kind, directUrl, { onUnavailable } = {}) {
  const uri = buildOfficeDesktopUri(kind, directUrl);
  if (!uri) {
    if (typeof onUnavailable === "function") {
      onUnavailable();
    }
    return false;
  }
  launchProtocolUri(uri, { onUnavailable });
  return true;
}
